import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useSendMessage, {
  generateClientMessageId,
  generateMessagePackage,
} from '@/hooks/conversations/use-send-message';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { clearLastPosition, useConversationSessionStore } from '@/stores/conversation-session';
import { addToast } from '@/stores/toast';
import { generateSessionKey } from '@/utils/session';
import { allChatSessionQueryOptions } from '../sessions/use-conversation-sessions';
import type { Collection } from './use-collections';

export default function useSendCollection(sessionType: ConversationType = 'session', sessionId = 0) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const mutation = useSendMessage();

  const sendCollection = async (collection: Collection) => {
    if (!collection || !sessionId) return;

    const sessionKey = generateSessionKey(sessionType, sessionId);
    const draft = useConversationSessionStore.getState().drafts[sessionKey];

    let content = collection.Content || '';
    if (collection.Type === ConversationMessageContentType.File) {
      content = `${collection.SmallPicturePath}|${content}`;
    }

    const message = generateMessagePackage(sessionType, sessionId, {
      Content: content,
      Quote: draft?.Quote,
      ContentType: collection.Type,
    });
    message.ClientMsgId = generateClientMessageId(message);
    await mutation.mutateAsync(message);

    await queryClient.refetchQueries(allChatSessionQueryOptions);

    // 删除草稿数据，触发表单重置
    useConversationSessionStore.getState().deleteDraft(sessionKey);

    clearLastPosition(
      sessionType === 'group'
        ? ROUTES.MESSAGES.GROUPS.$buildPath({ params: { id: sessionId } })
        : ROUTES.MESSAGES.SESSIONS.$buildPath({ params: { id: sessionId } }),
    );

    addToast({
      type: 'success',
      message: t('common.done'),
    });
  };

  return {
    sendCollection,
    ...mutation,
  };
}
